<template>
  <transition name="skm-fade">
    <div v-if="show" class="skm-overlay" @click.self="onCancel">
      <div class="skm-modal md-elevation-10">
        <div class="skm-header">
          <span class="skm-title">{{ L.stripe_modal_title || 'Stripe card payment' }}</span>
        </div>

        <div class="skm-body">
          <!-- When a key already exists, show its masked preview. -->
          <div v-if="hasKey && !editing" class="skm-existing">
            <p class="skm-existing-label">{{ L.stripe_modal_current_key || 'Active key' }}</p>
            <p class="skm-masked">{{ maskedLabel }}</p>
            <button class="skm-btn skm-btn--ghost" type="button" @click="startEdit">
              {{ L.stripe_modal_replace || 'Replace key' }}
            </button>
          </div>

          <!-- Key entry (new key, or replacing). -->
          <div v-else class="skm-entry">
            <p class="skm-warning">
              {{ L.stripe_modal_warning
                || 'Make sure this is the correct Stripe secret key — it is stored securely and will never be shown again. You can only replace it, not view it.' }}
            </p>

            <label class="skm-label">{{ L.stripe_modal_key_label || 'Stripe secret key' }}</label>
            <div class="skm-input-wrap">
              <input
                  ref="keyInput"
                  class="skm-input"
                  :type="reveal ? 'text' : 'password'"
                  :placeholder="'sk_live_… / sk_test_…'"
                  :disabled="saving"
                  autocomplete="off"
                  spellcheck="false"
                  v-model="keyValue"
                  @keyup.enter="validateAndSave" />
              <button
                  class="skm-eye"
                  type="button"
                  :title="reveal ? (L.hide || 'Hide') : (L.show || 'Show')"
                  @click="reveal = !reveal">
                <md-icon>{{ reveal ? 'visibility_off' : 'visibility' }}</md-icon>
              </button>
            </div>

            <p v-if="errorText" class="skm-error">{{ errorText }}</p>
          </div>
        </div>

        <div class="skm-actions">
          <button class="skm-btn skm-btn--secondary" type="button" :disabled="saving" @click="onCancel">
            {{ L.close_window || 'Cancel' }}
          </button>
          <button
              v-if="!hasKey || editing"
              class="skm-btn skm-btn--primary"
              type="button"
              :style="{ backgroundColor: brandColor }"
              :disabled="saving || !keyValue.trim()"
              @click="validateAndSave">
            {{ saving
              ? (L.stripe_modal_validating || 'Validating…')
              : (L.stripe_modal_save || 'Validate & save') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Stripe secret-key entry modal for a Checkout Profile.
 *
 * Flow:
 *  - If a key already exists, shows a masked "Live key · ••••4a9F" preview with a
 *    "Replace key" action; the plaintext key is never sent to the CRM.
 *  - Entering/replacing a key validates it against Stripe in the backend
 *    (checkout-profiles/stripe-key/validate-save). On success it is stored encrypted
 *    and Stripe is enabled; on an invalid key an inline error is shown and nothing is
 *    saved (so the toggle can't be enabled without a valid key).
 *
 * Emits:
 *  - 'saved' ({ exists, type, last4 }) after a successful validate+save
 *  - 'cancel' when the user closes without saving
 *
 * Uses the global doPost mixin (auto-encrypts) — needs `options` for token/apiUrl.
 */
export default {
  name: 'StripeKeyModal',
  props: {
    show: { type: Boolean, default: false },
    options: { type: Object, required: true },
    // Persisted profile id. Stripe key endpoints require a saved profile.
    profileId: { type: [Number, String], default: null },
    // Current masked status from the parent: { exists, type, last4 }.
    status: { type: Object, default: () => ({ exists: false, type: null, last4: null }) },
    brandColor: { type: String, default: '#635bff' }
  },
  data() {
    return {
      keyValue: '',
      reveal: false,
      saving: false,
      editing: false,
      errorText: ''
    };
  },
  computed: {
    L() { return this.options.Lang || {}; },
    hasKey() { return !!(this.status && this.status.exists); },
    maskedLabel() {
      const type = this.status && this.status.type;
      const last4 = (this.status && this.status.last4) || '';
      const typeLabel = type === 'live'
        ? (this.L.stripe_key_live || 'Live key')
        : (type === 'test' ? (this.L.stripe_key_test || 'Test key') : (this.L.stripe_key_generic || 'Key'));
      return `${typeLabel} · ••••${last4}`;
    }
  },
  // vue-material's .md-app-container has transform:translate3D(0,0,0), which traps
  // position:fixed descendants (the overlay becomes relative to the container and
  // can never cover the drawer/sidebar). Portal the modal element to <body> so the
  // overlay truly spans the viewport — same net effect as the App-root globalConfirm.
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  watch: {
    show(open) {
      if (open) {
        // Reset transient state every open; existing key stays masked until "Replace".
        this.keyValue = '';
        this.reveal = false;
        this.saving = false;
        this.editing = false;
        this.errorText = '';
      }
    }
  },
  methods: {
    startEdit() {
      this.editing = true;
      this.errorText = '';
      this.keyValue = '';
      this.$nextTick(() => { if (this.$refs.keyInput) this.$refs.keyInput.focus(); });
    },
    onCancel() {
      if (this.saving) return;
      this.$emit('cancel');
    },
    validateAndSave() {
      const key = this.keyValue.trim();
      if (!key || this.saving) return;
      if (!this.profileId) {
        this.errorText = this.L.stripe_modal_save_profile_first
          || 'Please save the profile once before adding a Stripe key.';
        return;
      }
      this.saving = true;
      this.errorText = '';
      const self = this;
      this.doPost(
        { data: { id: this.profileId, stripeSecretKey: key } },
        'checkout-profiles/stripe-key/validate-save',
        function (res) {
          self.saving = false;
          // doPost hands back the response envelope { msg, status, data } where status is
          // 'ok'|'error' (NOT 'success'). Match the codebase convention: error iff 'error'.
          if (res && res.status !== 'error') {
            const d = res.data || {};
            self.keyValue = '';
            self.$emit('saved', { exists: true, type: d.type || null, last4: d.last4 || null });
            return;
          }
          // Error: map the backend reason to a friendly message; nothing was saved.
          const reason = res && res.data && res.data.reason;
          let msg;
          if (reason === 'auth') {
            msg = self.L.stripe_modal_err_auth
              || 'Stripe rejected this key. Please check it and try again.';
          } else if (reason === 'network') {
            msg = self.L.stripe_modal_err_network
              || 'Could not reach Stripe to validate the key. Please try again.';
          } else {
            msg = self.L.stripe_modal_err_format
              || 'This does not look like a valid Stripe secret key (sk_live_… / sk_test_…).';
          }
          // In debug mode the backend includes the underlying Stripe reason — append it
          // so the admin sees exactly why validation failed (only present when dbg is on).
          const debugDetail = res && res.data && res.data.debugDetail;
          if (debugDetail) msg += '\n\n[debug] ' + debugDetail;
          self.errorText = msg;
        }
      );
    }
  }
};
</script>

<style scoped>
.skm-overlay {
  position: fixed; inset: 0;
  /* Must beat the left sidebar (z-index 111111111) — same pattern as
     #globalConfirmOverlay in App.vue. */
  z-index: 100000000001;
  background: rgba(20, 24, 33, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.skm-modal {
  width: 480px; max-width: 100%;
  background: #fff; border-radius: 14px; overflow: hidden;
  display: flex; flex-direction: column;
}
.skm-header { padding: 1.5rem 1.75rem 0.5rem; }
.skm-title { font-size: 1.2rem; font-weight: 700; color: #2c2c2c; }
.skm-body { padding: 0.5rem 1.75rem 1.25rem; }

.skm-warning {
  font-size: 0.85rem; color: #8a5a00; background: #fff6e5;
  border: 1px solid #ffe3a3; border-radius: 8px;
  padding: 0.7rem 0.85rem; margin: 0 0 1.1rem; line-height: 1.4;
}
.skm-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.3rem; }
.skm-input-wrap { position: relative; display: flex; align-items: center; }
.skm-input {
  width: 100%; height: 44px; border: 1px solid #dcdcdc; border-radius: 8px;
  background: #fafafa; padding: 0 2.75rem 0 0.85rem; font-size: 0.9rem; color: #202224;
  box-sizing: border-box; transition: border-color .15s ease, background .15s ease;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.skm-input:focus { outline: none; border-color: #635bff; background: #fff; }
.skm-input:disabled { opacity: 0.6; cursor: not-allowed; }
.skm-eye {
  position: absolute; right: 4px; height: 36px; width: 36px;
  border: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #6b7280;
}
.skm-eye:hover { color: #374151; }

.skm-error { color: #c0392b; font-size: 0.82rem; margin: 0.6rem 0 0; line-height: 1.4; white-space: pre-line; word-break: break-word; }

.skm-existing { text-align: left; }
.skm-existing-label { font-size: 0.78rem; font-weight: 600; color: #555; margin: 0 0 0.25rem; }
.skm-masked {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1rem; color: #202224; margin: 0 0 1rem;
  background: #f7f9fc; border: 1px solid #e7ecf3; border-radius: 8px; padding: 0.7rem 0.85rem;
}

.skm-actions {
  display: flex; justify-content: flex-end; gap: 0.75rem;
  padding: 0.75rem 1.75rem 1.5rem;
}
.skm-btn {
  border: none; border-radius: 8px; padding: 0.6rem 1.1rem;
  font-size: 0.88rem; font-weight: 600; cursor: pointer; min-width: 110px;
}
.skm-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.skm-btn--primary { color: #fff; }
.skm-btn--secondary { background: #eef1f5; color: #374151; }
.skm-btn--ghost { background: transparent; color: #635bff; min-width: 0; padding: 0.4rem 0; text-decoration: underline; }

.skm-fade-enter-active, .skm-fade-leave-active { transition: opacity .2s ease; }
.skm-fade-enter, .skm-fade-leave-to { opacity: 0; }
.skm-fade-enter-active .skm-modal { animation: skm-pop .22s ease; }
@keyframes skm-pop { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
</style>
